import { ChecklistCard, EnrollmentPeriod, EnrollmentStage, EnrollmentType } from 'types/enrollmentService';
import apolloClient from 'api/apolloClient';
import { BUILD_ENROLLMENT } from 'api/queries/enrollmentService';
import { Config } from 'types/domainServiceV2';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { brokerRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';

interface CardTextObject {
  title: string;
  text: string;
  employerTitle?: string;
  tutorialLink: string;
  redirectTo?: (periodId: string, groupId: string, cartId?: string) => string;
  action?: (periodId: string, submittedByUserName: string) => Promise<EnrollmentPeriod>;
  isAvailable?: (userRoles: SupportedUserRoles[]) => boolean;
}

type CardDataObjectType = {
  [K in ChecklistCard]: CardTextObject;
};

export const CARD_DATA: CardDataObjectType = Object.freeze({
  PROPOSAL_V2: {
    title: 'Proposal',
    employerTitle: 'Quote',
    text: 'View and download the plans selected to be offered to employees',
    redirectTo: (periodId: string, groupId: string, cartId?: string): string => {
      if (!cartId) {
        throw new Error('No Cart Id provided for the proposal redirect!');
      }
      return `/proposal-v2/${periodId}?groupId=${groupId}&cartId=${cartId}`;
    },
    tutorialLink: '#',
  },
  DEFAULT_BROKER: {
    title: 'Broker Agent',
    text: 'Review and complete agent info',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/agent-of-record/${groupId}/${periodId}`;
    },
    tutorialLink: '#',
    isAvailable: brokerRights,
  },
  /* Currently available only for Allied sponsor. */
  ALLIED_BROKER: {
    title: 'Broker Agent',
    text: 'Review and complete agent info',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/allied-agent-of-record/${groupId}/${periodId}`;
    },
    tutorialLink: '#',
    isAvailable: brokerRights,
  },
  SUNLIFE_BROKER: {
    title: 'Broker Agent',
    text: 'Review and complete agent info',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/sunlife-agent-of-record/${groupId}/${periodId}`;
    },
    tutorialLink: '#',
    isAvailable: brokerRights,
  },
  COMPANY_INFO: {
    title: 'Company Info',
    text: 'Add company details, contact, and address',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/company-info/${groupId}/${periodId}`;
    },
    tutorialLink: 'https://vimeo.com/415591879',
  },
  EMPLOYER_CONTRIBUTION: {
    title: 'Employer Contribution',
    text: 'Choose how much to cover for employees',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/contribution-form/${groupId}/${periodId}`;
    },
    tutorialLink: 'https://vimeo.com/415591879',
  },
  ADD_EMPLOYEES: {
    title: 'Add Employees',
    text: 'Add employees and monitor their progress',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/add-employees/${groupId}/${periodId}`;
    },
    tutorialLink: '#',
  },
  CENSUS_IMPORT: {
    title: 'Census Import',
    text: 'Use our template to load the employee Census',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/census/${groupId}/${periodId}`;
    },
    tutorialLink: '#',
  },
  EMPLOYEE_ELECTIONS: {
    title: 'Employee Elections',
    text: 'Enroll employees and monitor their progress',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/employee-elections/${periodId}?groupId=${groupId}`;
    },
    tutorialLink: '#',
  },
  FORMS: {
    title: 'Employer Forms',
    text: 'Fill out required carrier forms',
    redirectTo: (periodId: string): string => {
      return `/forms/${periodId}`;
    },
    tutorialLink: 'https://vimeo.com/415591879',
  },
  RENEWAL_FORMS: {
    title: 'Renewal Forms',
    text: 'Fill out required carrier forms',
    redirectTo: (periodId: string): string => {
      return `/renewal-forms/${periodId}`;
    },
    tutorialLink: '#',
  },
  REVIEW_SUBMIT: {
    title: 'Review & Submit',
    text: 'Review all information and submit enrollment to the carrier',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/review-submit/${periodId}?groupId=${groupId}`;
    },
    tutorialLink: '#',
  },
  BUILD: {
    title: 'Enrollment Packet',
    text: 'Please download all of your client materials to initiate enrollment',
    action: async (periodId: string, submittedByUserName: string): Promise<EnrollmentPeriod> => {
      const res = await apolloClient.mutate<{ completeEnrollment: EnrollmentPeriod }>({
        mutation: BUILD_ENROLLMENT,
        variables: { periodId, submittedByUserName },
      });
      if (!res.data) {
        throw new Error(`Build enrollment packet wasn't successful`);
      }
      return res.data.completeEnrollment;
    },
    redirectTo: (periodId: string): string => {
      return `/enrollment-packet/${periodId}`;
    },
    tutorialLink: '#',
  },
  ENROLLMENT_PACKET: {
    title: 'Enrollment Packet',
    text: 'Please download all of your client materials to initiate enrollment',
    redirectTo: (periodId: string): string => {
      return `/enrollment-packet/${periodId}`;
    },
    tutorialLink: '#',
  },
  SUMMARY_PAGE: {
    title: 'Transaction Summary',
    text: 'Browse transaction summary files for a group',
    redirectTo: (periodId: string): string => {
      return `/summary/${periodId}`;
    },
    tutorialLink: '#',
  },
  MEMBER_ID_CARDS: {
    title: 'Member ID Cards',
    text: 'Retrieve member ID cards',
    redirectTo: (periodId: string): string => {
      return `/additional-outputs/${periodId}`;
    },
    tutorialLink: '#',
  },
  WELCOME_KIT: {
    title: 'Welcome Kit',
    text: 'Retrieve EOCs, Highlights and Contracts',
    redirectTo: (periodId: string): string => {
      return `/docx-based-outputs/${periodId}`;
    },
    tutorialLink: '#',
  },
  PRODUCT_OUTPUTS: {
    title: 'Final Quote',
    text: 'Download all outputs of the confirmed quote packet',
    redirectTo: (periodId: string): string => {
      return `/product-outputs/${periodId}`;
    },
    tutorialLink: '#',
  },
  INDIVIDUAL_FORM: {
    title: 'Individual Form',
    text: 'Fill out required forms',
    redirectTo: (): string => '#',
    tutorialLink: '#',
  },
  ASSOCIATION_PROFILE: {
    title: 'Association Profile',
    text: 'Add details, contact, and address',
    redirectTo: (periodId: string): string => {
      return `/company-info/${periodId}`;
    },
    tutorialLink: '#',
  },
  REQUIRED_DOCUMENTS: {
    title: 'Upload Documents',
    text: 'Check to see if any documents need to be uploaded and/or manage collection',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/required-documents/${periodId}?groupId=${groupId}`;
    },
    tutorialLink: '#',
  },
  VIEW_ENROLLMENT_CHECKLIST: {
    title: 'View Enrollment Checklist',
    text: 'Review enrollment forms and censuses',
    redirectTo: (periodId: string): string => {
      return `/checklist/${periodId}`;
    },
    tutorialLink: '#',
  },
  DOWNLOAD_CONFIRMED_PROPOSAL: {
    title: 'Download Confirmed Proposal',
    text: 'Access documents for your confirmed enrollment plans',
    redirectTo: (periodId: string, groupId: string, cartId?: string): string => {
      if (!cartId) {
        throw new Error('No Cart Id provided for the download confirmed proposal redirect!');
      }
      return `/download-proposal/${periodId}/${groupId}/${cartId}`;
    },
    tutorialLink: '#',
  },
  POST_SUBMIT_DOCUMENTS: {
    title: 'View Documents',
    text: 'View, download, and manage documents after submission',
    redirectTo: (periodId: string, groupId: string): string => {
      return `/post-submit-documents/${periodId}?groupId=${groupId}`;
    },
    tutorialLink: '#',
  },
  CLOSE_DEAL: {
    title: 'Close Deal',
    text: 'Final review and complete group set up',
    redirectTo: (periodId: string): string => {
      return `/close-deal/${periodId}`;
    },
    tutorialLink: '#',
  },
});

export const notEditableEnrollmentStages = ['ARCHIVED', 'SOLD'];

export const isEnrollmentPeriodNotEditable = (stage: EnrollmentStage): boolean => {
  return notEditableEnrollmentStages.includes(stage);
};

export const isEnrollmentPeriodUnavailableToResubmit = (
  enrollmentRateSegment: RateSegment,
  enrollmentStage: EnrollmentStage,
  enrollmentType: EnrollmentType,
  hasPostSubmitChecklist: boolean,
  domainConfig: Config,
): boolean => {
  return (
    (domainConfig[enrollmentRateSegment].resubmitIneligible &&
      enrollmentType === EnrollmentType.NEW_SALE &&
      hasPostSubmitChecklist) ||
    notEditableEnrollmentStages.includes(enrollmentStage)
  );
};

const stepsWithFiles = [
  ChecklistCard.SUMMARY_PAGE,
  ChecklistCard.ENROLLMENT_PACKET,
  ChecklistCard.PRODUCT_OUTPUTS,
  ChecklistCard.MEMBER_ID_CARDS,
  ChecklistCard.WELCOME_KIT,
  ChecklistCard.DOWNLOAD_CONFIRMED_PROPOSAL,
];

export const isEnrollmentFilesStep = (card: ChecklistCard): boolean => {
  return stepsWithFiles.includes(card);
};
